import React, { useEffect } from "react"
import { graphql, Link } from "gatsby"
import SEO from "../components/seo"

export const pageQuery = graphql`
  query HomeQuery($id: String!) {
    markdownRemark(id: { eq: $id }) {
      id
      frontmatter {
        title
        hero {
          headline
          subheadline
          primaryCta
          secondaryCta
        }
        trust {
          item
        }
        problem {
          heading
          description
          closing
        }
        work {
          image {
            publicURL
          }
          title
          role
          description
        }
        process {
          number
          title
          description
        }
        caseStudy {
          image {
            publicURL
          }
          heading
          project
          problem
          approach
          result
        }
        about {
          p1
          p2
          p3
        }
        cta {
          heading
          subheading
          buttonText
          email
        }
      }
    }
  }
`

const HomePage = ({ data }) => {
  const { markdownRemark } = data
  const { frontmatter } = markdownRemark
  const fm = frontmatter

  useEffect(() => {
    // 1. Navigation background on scroll
    const navbar = document.querySelector(".navbar")
    const handleScroll = () => {
      if (window.scrollY > 50) {
        navbar.classList.add("scrolled")
      } else {
        navbar.classList.remove("scrolled")
      }
    }
    window.addEventListener("scroll", handleScroll)

    // 2. Hero Background Slow Zoom
    const heroBg = document.querySelector(".hero-bg")
    if (heroBg) {
      setTimeout(() => {
        heroBg.classList.add("loaded")
      }, 100)
    }

    // 3. Intersection Observer for Fade-Up animations
    const observerOptions = {
      root: null,
      rootMargin: "0px",
      threshold: 0.15,
    }

    const observer = new IntersectionObserver((entries, observer) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add("visible")
          observer.unobserve(entry.target)
        }
      })
    }, observerOptions)

    const animatedElements = document.querySelectorAll(".fade-up, .img-reveal")
    animatedElements.forEach((el) => observer.observe(el))

    return () => {
      window.removeEventListener("scroll", handleScroll)
      observer.disconnect()
    }
  }, [])

  return (
    <React.Fragment>
      <SEO title={fm.title} />

      {/* Navigation */}
      <nav className="navbar">
        <div className="container nav-container">
          <Link to="/" className="nav-brand">
            Syed Mohammad Talib
          </Link>
          <div className="nav-menu">
            <a href="#work" className="nav-link">
              Work
            </a>
            <a href="#process" className="nav-link">
              Process
            </a>
            <a href="#about" className="nav-link">
              About
            </a>
            <a href="#contact" className="btn btn-outline" style={{ padding: "10px 24px" }}>
              Start a Project
            </a>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <header className="hero">
        <div className="hero-bg"></div>
        <div className="hero-overlay"></div>
        <div className="container">
          <div className="hero-content fade-up">
            <h1 className="fade-up delay-100">{fm.hero?.headline}</h1>
            <p className="fade-up delay-200">{fm.hero?.subheadline}</p>
            <div className="hero-actions fade-up delay-300">
              <a href="#contact" className="btn btn-primary">
                {fm.hero?.primaryCta || "Start a Project"}
              </a>
              <a href="#work" className="btn btn-outline">
                {fm.hero?.secondaryCta || "View Work"}
              </a>
            </div>
          </div>
        </div>
        <div className="scroll-indicator fade-up delay-400">
          <span className="scroll-line"></span>
        </div>
      </header>

      {/* Trust Strip */}
      <section className="trust-strip">
        <div className="container">
          <ul className="trust-list fade-up">
            {fm.trust && fm.trust.map((t, i) => (
              <li key={i}>{t.item}</li>
            ))}
          </ul>
        </div>
      </section>

      {/* Problem Framing */}
      <section className="section">
        <div className="container problem-framing fade-up">
          <h2>{fm.problem?.heading}</h2>
          <p>{fm.problem?.description}</p>
          <p className="closing-line fade-up delay-200">{fm.problem?.closing}</p>
        </div>
      </section>

      {/* Selected Work */}
      <section id="work" className="section">
        <div className="container">
          {fm.work && fm.work.map((w, i) => (
            <div className="work-item fade-up" key={i}>
              <div className="work-image img-reveal">
                <img src={w.image?.publicURL || w.image} alt={w.title} loading="lazy" />
              </div>
              <div className="work-details">
                <div>
                  <h3 className="work-title">{w.title}</h3>
                  <span className="work-role">{w.role}</span>
                </div>
                <div>
                  <p>{w.description}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Process Section */}
      <section id="process" className="section">
        <div className="container">
          <h2 className="fade-up" style={{ marginBottom: "64px" }}>
            How I Approach YouTube Editing
          </h2>
          <div className="process-list">
            {fm.process && fm.process.map((p, i) => (
              <div className="process-item fade-up" key={i}>
                <div className="process-number">{p.number}</div>
                <div>
                  <h3>{p.title}</h3>
                  <p>{p.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Case Study */}
      <section className="section case-study">
        <div className="container case-study-grid">
          <img
            src={fm.caseStudy?.image?.publicURL || fm.caseStudy?.image}
            alt="Case Study"
            className="case-image img-reveal"
            loading="lazy"
          />
          <div className="case-content fade-up delay-200">
            <h2>{fm.caseStudy?.heading}</h2>
            <div className="case-detail">
              <span className="case-label">Project</span>
              <p className="text-primary" style={{ color: "var(--text-primary)" }}>
                {fm.caseStudy?.project}
              </p>
            </div>
            <div className="case-detail">
              <span className="case-label">Problem</span>
              <p>{fm.caseStudy?.problem}</p>
            </div>
            <div className="case-detail">
              <span className="case-label">Approach</span>
              <p>{fm.caseStudy?.approach}</p>
            </div>
            <div className="case-detail">
              <span className="case-label">Result</span>
              <p>{fm.caseStudy?.result}</p>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="section about">
        <div className="container about-content fade-up">
          <p>{fm.about?.p1}</p>
          <p>{fm.about?.p2}</p>
          <p
            style={{
              fontFamily: "var(--font-sans)",
              fontSize: "16px",
              color: "var(--text-secondary)",
              marginTop: "32px",
            }}
          >
            {fm.about?.p3}
          </p>
        </div>
      </section>

      {/* Final CTA */}
      <section id="contact" className="final-cta">
        <div className="container fade-up">
          <h2>{fm.cta?.heading}</h2>
          <p
            className="text-accent"
            style={{
              textTransform: "uppercase",
              letterSpacing: "0.03em",
              fontWeight: 500,
              fontSize: "14px",
            }}
          >
            {fm.cta?.subheading}
          </p>
          <a href={`mailto:${fm.cta?.email}`} className="btn btn-primary">
            {fm.cta?.buttonText}
          </a>
        </div>
      </section>
    </React.Fragment>
  )
}

export default HomePage
